/**
 * Journal Entries Page — helpers, constants, and shared types
 */

import { createElement } from 'react'

import { BookOpen, FileText, Calculator, ArrowRightLeft } from 'lucide-react'

import { ENTRY_STATUS_LABELS, ENTRY_TYPE_LABELS } from '../../constants/labels'
import {
  getStatusColor,
  type EntryStatus,
  type EntryType,
} from '../../services/journal-entry-service'
import { formatMoney as formatMoneyUtil } from '../../utils/formatters'

// ── DisplayEntry type ─────────────────────────────────────────────────────────

/**
 * Unified display shape that works for both API and local-db entries.
 */
export interface DisplayEntry {
  id: string
  entryNumber: string
  entryDate: string
  entryType: string
  memo: string | null
  status: string
  /** Transaction currency totals (original entry currency) */
  totalDebit: { amount: number; currency: string }
  totalCredit: { amount: number; currency: string }
  /** Base currency totals (converted to org base currency) */
  totalDebitBase?: { amount: number; currency: string }
  totalCreditBase?: { amount: number; currency: string }
  /** True when entry involves foreign currency */
  _hasForex?: boolean
  isLocal?: boolean
}

// ── Currency helpers ──────────────────────────────────────────────────────────

/**
 * Returns true when the entry involves a currency different from baseCurrency,
 * either via the explicit _hasForex flag or by inspecting the total currencies.
 */
export function hasForeignCurrency(entry: DisplayEntry, baseCurrency: string): boolean {
  if (entry._hasForex) { return true }
  return (
    entry.totalDebit.currency !== baseCurrency ||
    entry.totalCredit.currency !== baseCurrency
  )
}

/**
 * Returns true when the transaction-side total looks like a bogus cross-currency
 * sum (i.e. a foreign-labeled amount that is numerically within 5 % of the base
 * amount, meaning the server summed mixed currencies together).
 */
export function isBogusTotal(
  txn: { amount: number; currency: string },
  base: { amount: number; currency: string } | undefined,
  baseCurrency: string,
): boolean {
  if (txn.currency === baseCurrency) { return false }
  if (!base || base.amount === 0) { return false }
  if (txn.currency === base.currency) { return false }
  const ratio = txn.amount / base.amount
  return ratio > 0.95 && ratio < 1.05
}

// ── Money / date formatters ───────────────────────────────────────────────────

export function formatMoney(amount: number, currency: string): string {
  return formatMoneyUtil({ amount, currency })
}

export function formatDate(dateStr: string, locale: string): string {
  const date = new Date(dateStr)
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }
  return date.toLocaleDateString(locale, options)
}

// ── Filter helpers ────────────────────────────────────────────────────────────

export function getStatusBadgeVariant(status: string): 'success' | 'warning' | 'error' | 'secondary' {
  const color = getStatusColor(status as EntryStatus)
  if (color === 'green') { return 'success' }
  if (color === 'yellow') { return 'warning' }
  if (color === 'red') { return 'error' }
  return 'secondary'
}

export function buildStatusOptions(): { value: string; label: string }[] {
  return Object.entries(ENTRY_STATUS_LABELS).map(([value, label]) => ({ value, label }))
}

export function buildTypeOptions(): { value: string; label: string }[] {
  return Object.entries(ENTRY_TYPE_LABELS).map(([value, label]) => ({ value, label }))
}

// ── Page info data (constants only — icons are React elements, kept pure) ─────

export const JOURNAL_FEATURES = [
  {
    icon: createElement(BookOpen, { className: 'w-4 h-4' }),
    title: 'Double-Entry Bookkeeping',
    description: 'Every transaction is recorded with balanced debits and credits to maintain accurate books',
  },
  {
    icon: createElement(FileText, { className: 'w-4 h-4' }),
    title: 'Multiple Entry Types',
    description: 'Standard, adjusting, closing, and reversing entries for different accounting needs',
  },
  {
    icon: createElement(Calculator, { className: 'w-4 h-4' }),
    title: 'Multi-Currency Support',
    description: 'Record entries in any currency with automatic base currency conversion',
  },
  {
    icon: createElement(ArrowRightLeft, { className: 'w-4 h-4' }),
    title: 'Draft & Post Workflow',
    description: 'Create entries as drafts, review them, then post to finalize and update account balances',
  },
]

export const JOURNAL_STEPS = [
  {
    title: 'Create a new entry',
    description: 'Click "New Entry" to open the journal entry form with debit and credit lines',
  },
  {
    title: 'Add line items',
    description: 'Select accounts, enter amounts, and add a memo to describe the transaction',
  },
  {
    title: 'Review and post',
    description: 'Save as draft to review later, or post immediately to update your account balances',
  },
]

export const JOURNAL_WHAT_IS_IT = {
  title: 'What are Journal Entries?',
  description:
    'Journal entries are the fundamental building blocks of your accounting records. Each entry captures a financial transaction with balanced debit and credit amounts across one or more accounts.',
}

// ── Re-export label maps so callers don't need a second import ────────────────
export { ENTRY_STATUS_LABELS, ENTRY_TYPE_LABELS }
export type { EntryStatus, EntryType }
